/**
 * Select and ask (plan 7.4): what the canvas hands the chat.
 *
 * - Ask Tabbio arms the selection: the composer shows the chip "Editing:
 *   <first 60 characters>" with a clear button and takes focus, and the
 *   next message carries `artifactContext { artifactId, versionId,
 *   selection }`.
 * - A quick action (Shorter, Longer, More casual, More formal, Suggest
 *   edits, Fix with Tabbio) sends its localized instruction at once with the
 *   same context: the chat thread on screen picks it up
 *   (`ArtifactAskBridge`) and appends it as the user's message.
 *
 * Module state like `site-compose.ts`, because the canvas panel (drawn by
 * the shell, or a native route over the chat), the composers and the chat
 * runtime (memoized per thread, outside React) all read it.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { ArtifactContext, ArtifactSelection } from '@tabbio/types';

/** The chip's words: at most 60 characters of the selection. */
export const ARTIFACT_ASK_CHIP_CHARS = 60;

export type ArtifactAskChip = {
  context: ArtifactContext;
  /** What the chip quotes (the selected text, or the image's title). */
  label: string;
};

export type ArtifactQuickSend = {
  id: number;
  text: string;
  context: ArtifactContext;
  /** The artifact's thread, so another chat never takes it. */
  threadId: string | null;
  createdAt: number;
};

export type ArtifactAskState = {
  chip: ArtifactAskChip | null;
  /** Grows with each Ask Tabbio, so the composer focuses again. */
  focusRequest: number;
  pending: ArtifactQuickSend | null;
};

/** A quick action nobody picked up in this long is dropped (a chat that never showed). */
export const ARTIFACT_QUICK_SEND_TTL_MS = 60_000;

let state: ArtifactAskState = { chip: null, focusRequest: 0, pending: null };
/** The context of the message being sent now (a quick action), read once. */
let oneShot: ArtifactContext | null = null;
let nextId = 1;
const listeners = new Set<() => void>();

function set(next: ArtifactAskState) {
  state = next;
  for (const listener of listeners) listener();
}

export function subscribeArtifactAsk(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function readArtifactAsk(): ArtifactAskState {
  return state;
}

export function useArtifactAsk(): ArtifactAskState {
  return useSyncExternalStore(subscribeArtifactAsk, readArtifactAsk, readArtifactAsk);
}

/** "Editing: <first 60 characters>" quotes this, trimmed and on one line. */
export function artifactAskChipText(label: string): string {
  const flat = label.replace(/\s+/g, ' ').trim();
  return flat.length > ARTIFACT_ASK_CHIP_CHARS ? `${flat.slice(0, ARTIFACT_ASK_CHIP_CHARS).trimEnd()}…` : flat;
}

/** Ask Tabbio: the chip, and focus to the composer. */
export function armArtifactAsk(context: ArtifactContext, label: string) {
  set({ ...state, chip: { context, label }, focusRequest: state.focusRequest + 1 });
}

/** The chip's clear button. */
export function clearArtifactAsk() {
  if (!state.chip) return;
  set({ ...state, chip: null });
}

/**
 * A quick action: the instruction goes out at once with the context. The
 * chip (if any) is spent too, since the selection went with this message.
 */
export function sendArtifactQuickAction(input: {
  text: string;
  context: ArtifactContext;
  threadId: string | null;
}) {
  const pending: ArtifactQuickSend = {
    id: nextId++,
    text: input.text,
    context: input.context,
    threadId: input.threadId,
    createdAt: Date.now(),
  };
  set({ ...state, chip: null, pending });
}

/**
 * The chat thread on screen takes the pending quick action (once): its
 * context rides on the message it appends. `threadId` is the thread shown
 * (null for a new chat); a quick action for another thread is left alone.
 */
export function takeArtifactQuickAction(threadId: string | null, now = Date.now()): ArtifactQuickSend | null {
  const pending = state.pending;
  if (!pending) return null;
  if (now - pending.createdAt > ARTIFACT_QUICK_SEND_TTL_MS) {
    set({ ...state, pending: null });
    return null;
  }
  if (pending.threadId && threadId && pending.threadId !== threadId) return null;
  oneShot = pending.context;
  set({ ...state, pending: null });
  return pending;
}

/**
 * Read at send time by the chat runtime: a quick action's context first,
 * then the Ask Tabbio chip (spent by this message). Undefined when neither.
 */
export function consumeArtifactAskContext(): ArtifactContext | undefined {
  if (oneShot) {
    const context = oneShot;
    oneShot = null;
    return context;
  }
  const chip = state.chip;
  if (!chip) return undefined;
  set({ ...state, chip: null });
  return chip.context;
}

/**
 * The chat thread's side (web `thread.web.tsx`, native `thread.tsx`): a
 * pending quick action is appended as the user's message; its context rides
 * along through `consumeArtifactAskContext`. `threadId` is the thread shown
 * (null for a new chat, or on web where one chat sits beside the panel).
 */
export function useArtifactQuickActions(
  threadId: string | null,
  append: (text: string) => void,
  enabled = true,
) {
  const { pending } = useArtifactAsk();
  const appendRef = useRef(append);
  appendRef.current = append;
  useEffect(() => {
    if (!enabled || !pending) return;
    const taken = takeArtifactQuickAction(threadId);
    if (taken) appendRef.current(taken.text);
  }, [enabled, pending, threadId]);
}

/** The composer's side: focus the input on each Ask Tabbio. */
export function useArtifactAskFocus(focus: () => void) {
  const { focusRequest } = useArtifactAsk();
  const seen = useRef(focusRequest);
  const focusRef = useRef(focus);
  focusRef.current = focus;
  useEffect(() => {
    if (focusRequest === seen.current) return;
    seen.current = focusRequest;
    focusRef.current();
  }, [focusRequest]);
}

/** The selection of a page element (plan 5.3: at most 2,000 characters). */
export function pageSelection(elementId: string, text: string): ArtifactSelection {
  return { kind: 'page', elementId, text: text.slice(0, 2_000) };
}

/** The selection of a document block (-1 is the section heading). */
export function documentSelection(sectionIndex: number, blockIndex: number, text: string): ArtifactSelection {
  return {
    kind: 'document',
    sectionIndex: Math.max(0, Math.min(11, sectionIndex)),
    blockIndex: Math.max(-1, Math.min(39, blockIndex)),
    text: text.slice(0, 2_000),
  };
}

export function resetArtifactAskForTests() {
  state = { chip: null, focusRequest: 0, pending: null };
  oneShot = null;
  nextId = 1;
  listeners.clear();
}
